import { TypographyH1, TypographyH3 } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { FormattedMessage } from 'react-intl';

export interface EvidencePowerUserRightPanelProps {
  /** How many times the user has submitted a message to Evidence. */
  count: number;
  /** True while the count is being loaded. Reserves space with a skeleton so layout doesn't shift. */
  isLoading?: boolean;
  className?: string;
}

/**
 * Right-hand panel for the Evidence power-user upgrade modal.
 * Shows a large personalised usage count inside a rounded surface, with a caption beneath.
 *
 * Design: https://www.figma.com/design/MSqFiRMYvicVZlaiWjmFG3/Trials---upgrades?node-id=9562-47495
 *
 * Note: the outer panel background and decorative pattern are provided
 * by `HeidiUpgradeBaseModal` via `UpgradeTestimonialBg`, so we only render centered content.
 */
export const EvidencePowerUserRightPanel = ({
  count,
  isLoading = false,
  className,
}: EvidencePowerUserRightPanelProps) => {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-6', className)}>
      <div
        className="flex h-[156px] w-[199px] items-center justify-center rounded-2xl bg-background-secondary p-2.5"
        data-testid="evidence-power-user-count"
      >
        {isLoading ? (
          <div
            className="size-24 animate-pulse rounded-md bg-background-secondary-hover"
            aria-hidden
          />
        ) : (
          <TypographyH1
            className="text-[96px] tabular-nums leading-none tracking-tight text-text-secondary"
            aria-label={`${count} times used Evidence in your trial`}
          >
            {count}
          </TypographyH1>
        )}
      </div>
      <TypographyH3 className="max-w-[276px] text-balance text-center text-text-primary">
        <FormattedMessage
          defaultMessage="Times you've used Evidence in your trial"
          id="rwVuNsykeb"
          description="Caption shown beneath the personalised Evidence usage count on the Evidence power-user upgrade modal"
        />
      </TypographyH3>
    </div>
  );
};
